import { memo, useState } from 'react';
import {
  Avatar,
  Box,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import { BoardProjectSurface, BoardMark } from './BoardSurfaces';
import type { BoardCardCoreFragment } from '../../../generated/graphql';

interface BoardCardProps {
  card: BoardCardCoreFragment;
  columnId: string;
  columns: { _id: string; name: string }[];
  onDelete: (cardId: string) => void;
  onMoveCard: (cardId: string, toColumnId: string) => void;
  onOpenProject: (projectId: string) => void;
}

function BoardCardImpl({
  card,
  columnId,
  columns,
  onDelete,
  onMoveCard,
  onOpenProject,
}: BoardCardProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const otherColumns = columns.filter((column) => column._id !== columnId);
  const hasAccess = card.project !== null && card.project !== undefined;
  const daysAgo = Math.floor(
    (new Date().getTime() - new Date(card.addedAt).getTime()) /
      (1000 * 60 * 60 * 24)
  );
  const ownerUser = card.project?.ownerUser;
  const ownerName = ownerUser
    ? `${ownerUser.firstName} ${ownerUser.lastName}`.trim()
    : '';
  const ownerInitials = ownerUser
    ? `${ownerUser.firstName?.[0] ?? ''}${ownerUser.lastName?.[0] ?? ''}`.toUpperCase()
    : '?';
  const versionCount = card.project?.versionCount;

  return (
    <BoardProjectSurface
      variant="panel"
      sx={{
        mb: 1.25,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CardActionArea
        disabled={!hasAccess}
        onClick={() => hasAccess && onOpenProject(card.projectId)}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {hasAccess ? (
            <>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={1}
                pr={3}
                sx={{ display: { xs: 'none', md: 'flex' } }}
              >
                <BoardMark sx={{ width: 30, height: 30 }}>
                  <FolderOutlinedIcon sx={{ fontSize: 17 }} />
                </BoardMark>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontSize: '0.6rem', letterSpacing: '0.14em' }}
                >
                  Project
                </Typography>
              </Box>
              <Typography
                variant="subtitle2"
                title={card.project!.name}
                sx={{
                  fontWeight: 650,
                  fontSize: '0.95rem',
                  lineHeight: 1.45,
                  minHeight: 0,
                  pr: { xs: 4, md: 0 },
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  mb: 1.25,
                }}
              >
                {card.project!.name}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                {(versionCount ?? 0) > 0 && (
                  <Chip
                    variant="soft"
                    size="small"
                    role="img"
                    aria-label={`${versionCount} ${versionCount === 1 ? 'version' : 'versions'}`}
                    icon={<LayersOutlinedIcon />}
                    label={`${versionCount} ${versionCount === 1 ? 'version' : 'versions'}`}
                    sx={{ height: 24, fontSize: '0.65rem' }}
                  />
                )}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 'auto', fontSize: '0.65rem' }}
                >
                  {daysAgo === 0 ? 'Added today' : `Added ${daysAgo}d ago`}
                </Typography>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{ pt: 1.25, borderTop: 1, borderColor: 'divider' }}
              >
                <Tooltip title={ownerName || 'Owner unavailable'}>
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: '0.6rem',
                      fontWeight: 700,
                    }}
                  >
                    {ownerInitials || '?'}
                  </Avatar>
                </Tooltip>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {ownerName || 'Owner unavailable'}
                </Typography>
              </Box>
            </>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
            >
              No access to this project
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
      <IconButton
        size="small"
        aria-label="card menu"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          minWidth: { xs: 44, md: 'auto' },
          minHeight: { xs: 44, md: 'auto' },
        }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        {otherColumns.length > 0 && [
          <ListSubheader
            key="move-hdr"
            sx={{ bgcolor: 'transparent', lineHeight: 2 }}
          >
            Move to
          </ListSubheader>,
          ...otherColumns.map((column) => (
            <MenuItem
              key={column._id}
              onClick={() => {
                onMoveCard(card._id, column._id);
                setAnchorEl(null);
              }}
            >
              {column.name}
            </MenuItem>
          )),
          <Divider key="move-div" />,
        ]}
        <MenuItem
          onClick={() => {
            onDelete(card._id);
            setAnchorEl(null);
          }}
        >
          Remove
        </MenuItem>
      </Menu>
    </BoardProjectSurface>
  );
}

export const BoardCard = memo(BoardCardImpl);
